import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  CenterModal, ModalSize, Button, ButtonVariant,
  ButtonSize
} from "@repo/design-system/components/ui";
import { FormBuilder } from "@repo/design-system/components/forms";
import {
  getFieldData, getValue, setDropDownOptionField,
  removeFormFields
} from "@repo/design-system/components/forms/utils";

// Service's
import { ToastService } from "@/services";
import {
  useGetDropdownAllQuery, useSearchAcademicDetailsMutation,
  useSaveAcademicDetailsMutation,
} from "@gyandiksha/shared-ui/app/services";
import { buildInitialFormConfig } from "../config/profile.config.js";

import "./CompleteProfileModal.scss";

const mapToOption = (item) => ({
  value: String(item.id ?? ""),
  label: item.value ?? item.name ?? String(item.id ?? ""),
});

const isSchoolLabel = (label = "") => /10th|12th/i.test(label);

const withoutEnrollment = (fields) =>
  fields.filter((f) => f.id !== "enrollmentNumber");

const CompleteProfileModal = ({ isOpen, onClose, onSave }) => {
  const formRef = useRef();
  const [formConfig, setFormConfig] = useState(() => {
    let config = structuredClone(buildInitialFormConfig());
    config = removeFormFields(config, ["seatNumber"]);
    return config;
  });
  const [searchResult, setSearchResult] = useState(null);
  const [searchPayload, setSearchPayload] = useState(null);

  const { data: dropdownData } = useGetDropdownAllQuery(undefined, {
    skip: !isOpen,
  });
  const qualifData = dropdownData?.data?.programLevel ?? [];

  const [searchAcademicDetails, { isLoading: isSearching }] =
    useSearchAcademicDetailsMutation();
  const [saveAcademicDetails, { isLoading: isSaving }] =
    useSaveAcademicDetailsMutation();

  const getQualifLabel = useCallback(
    (value) =>
      qualifData.map(mapToOption).find((o) => o.value === String(value))
        ?.label ?? "",
    [qualifData],
  );

  useEffect(() => {
    if (!qualifData.length) return;
    setFormConfig((prev) => {
      const updated = structuredClone(prev);
      setDropDownOptionField(
        qualifData,
        "id",
        "value",
        updated,
        "qualification",
      );
      return updated;
    });
  }, [qualifData]);

  const handleFormChange = useCallback(
    (updatedData) => {
      const selectedQualifValue = getValue(updatedData.qualification);
      if (selectedQualifValue === 5 || selectedQualifValue === 6) {
        setFormConfig((prev) => {
          let config = structuredClone(prev);
          config = removeFormFields(config, ["enrollmentNumber"]);
          return config;
        });
      }
      setSearchResult(null);
    },
    [],
  );

  const handleSearch = async () => {
    const formData = formRef.current?.getValidatedFormData();
    if (!formData) return;

    const qualifValue = getValue(formData.qualification);
    const isSchool = isSchoolLabel(getQualifLabel(qualifValue));
    const inputValue =
      (isSchool ? formData.seatNumber : formData.enrollmentNumber) ?? "";

    const payload = {
      programLevelId: Number(qualifValue),
      enrollmentNo: inputValue.trim(),
    };

    setSearchResult(null);
    setSearchPayload(payload);

    const result = await searchAcademicDetails(payload);

    if (result?.data) {
      const resData = result.data?.data ?? result.data;
      setSearchResult(resData?.academic ?? resData);
    }
  };

  const handleSave = async () => {
    if (!searchResult || !searchPayload) return;

    const result = await saveAcademicDetails({ ...searchPayload, saveFlag: true });

    if (result?.data) {
      if (result.data?.message) {
        ToastService.success({ description: result.data.message });
      }
      onSave && onSave(result.data);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchResult(null);
    setSearchPayload(null);
    setFormConfig((prev) => {
      const reset = structuredClone(buildInitialFormConfig());
      const prevQualifField = getFieldData(prev, "qualification");
      const resetQualifField = getFieldData(reset, "qualification");
      if (
        prevQualifField &&
        resetQualifField &&
        prevQualifField.fieldSetting?.options?.length
      ) {
        resetQualifField.fieldSetting.options =
          prevQualifField.fieldSetting.options;
      }
      reset.section[0].rowGroup[0].fields = withoutEnrollment(
        reset.section[0].rowGroup[0].fields,
      );
      return reset;
    });
    onClose();
  };

  const renderResultRows = () => {
    if (!searchResult) return null;

    const EXPECTED_FIELDS = [
      {
        label: "Qualification Name",
        keys: ["programmeLevelName", "qualificationName"],
      },
      { label: "Institute", keys: ["instituteName", "schoolName"] },
      { label: "Degree/Program", keys: ["programmeName", "courseName"] },
      { label: "Branch/Stream", keys: ["branchName", "streamName"] },
      {
        label: "Enrollment/Seat Number",
        keys: ["enrollmentNo", "seatNumber", "seatNo"],
      },
      {
        label: "Admission/Passing Year",
        keys: ["admittedYear", "passingYear"],
      },
      { label: "Status", keys: ["activeStatus", "passingStatus"] },
    ];

    return EXPECTED_FIELDS.map((field) => {
      const matchingKey = field.keys.find((k) => searchResult[k] !== undefined);
      const value = matchingKey ? searchResult[matchingKey] : null;
      const display =
        value === null || value === undefined || value === ""
          ? "-"
          : String(value);

      return (
        <div className="cp-info-item" key={field.label}>
          <span className="cp-info-label">{field.label} :</span>
          <span className="cp-info-value">{display}</span>
        </div>
      );
    });
  };

  return (
    <CenterModal
      isOpen={isOpen}
      handleToggleModal={handleClose}
      isApprovalValidate={false}
      modalTitle="Complete Your Profile"
      modelSizeClass={ModalSize.WD50}
      footerChildren={
        <div className="cp-footer">
          <Button
            variant={ButtonVariant.DANGER}
            size={ButtonSize.SM}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            onClick={handleSave}
            loading={isSaving}
            disabled={!searchResult}
          >
            Save &amp; Continue
          </Button>
        </div>
      }
    >
      <div className="cp-modal-body">
        <div className="cp-form-card">
          <div className="cp-form-header">
            <div className="cp-section-title">
              <span className="cp-section-bar" />
              <div>
                <h3>Complete Your Profile</h3>
                <p>
                  Add your academic details to complete your student profile
                  information.
                </p>
              </div>
            </div>
          </div>

          <div className="cp-formbuilder-wrapper">
            <FormBuilder
              ref={formRef}
              config={formConfig}
              onFormDataChange={handleFormChange}
            />
          </div>

          <div
            className="cp-search-button-row d-flex justify-content-end"
            style={{ marginTop: "-8px" }}
          >
            <Button
              id="cp-search-btn"
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              leftIcon="lucide:search"
              onClick={handleSearch}
              loading={isSearching}
            >
              Search
            </Button>
          </div>
        </div>

        {searchResult && (
          <div className="cp-info-card">
            <div className="cp-info-row">{renderResultRows()}</div>
          </div>
        )}
      </div>
    </CenterModal>
  );
};

CompleteProfileModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
};

export default CompleteProfileModal;
